import { useCallback, useContext, useMemo } from 'react';
import type { ProjectConferenceTagWithMetadata } from '@trinity/chat/api/interfaces/projects';
import { Modal, Header } from '@trinity/chat-components/components/Modal/index.js';
import type { ModalProps } from '@trinity/chat-components/components/Modal/Modal.js';
import { Button } from '@trinity/chat-components/components/presentation/Button/index.js';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/Button.ActivityIndicator.js';
import { TagFormContext, UpdateTagContext, type TagFormData } from './Context';
// import { useUpdateTagMutation } from '@/components/Project.Tags/hooks';
import styles from './style/CreateTagModal.module.css';
import { TagForm } from './TagForm';
import { TagFormContainer } from './TagFormContainer';

type Props = {
  tag: ProjectConferenceTagWithMetadata;
  hasChildren: boolean;
} & Pick<ModalProps, 'open' | 'onClose'>;

export const EditTag = ({ open, onClose, tag, hasChildren }: Props) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Header>Edit Tag</Header>
      <TagFormContainer defaultState={{ parentId: tag.parent?.id, title: tag.base.name, color: tag.base.color, category: tag.base.tagCategory, includeSummary: tag.includeSummary }}>
        <ModalContent
          onClose={onClose}
          projectTagId={tag.id}
          hasChildren={hasChildren} />
      </TagFormContainer>
    </Modal>
  );
};

type ContentProps = Pick<ModalProps, 'onClose'> &
{
  projectTagId: number;
  hasChildren: boolean;
};

const ModalContent = ({ onClose, projectTagId, hasChildren }: ContentProps) => {
  const { data } = useContext(TagFormContext);

  const canSave = useMemo(() => tagFormIsValid(data), [data]);

  const mutation = useContext(UpdateTagContext);

  const updateTag = useCallback(() => {
    mutation.mutateAsync({
      title: data.title,
      color: data.color,
      includeSummary: data.includeSummary,
      parentId: data.parentId ?? null,
      tagCategory: data.category,
      tagId: projectTagId,
    }).then(onClose);
  }, [mutation, onClose, projectTagId, data]);

  return (
    <>
      <TagForm disableParent={hasChildren} excludeParentIds={[projectTagId]} />
      <div className={styles.actions}>
        <Button
          variant="brick"
          color="destructive"
          onClick={onClose}>
          Cancel
        </Button>
        <ButtonActivityIndicator
          disabled={!canSave}
          implicitDisable={false}
          loading={mutation.isPending}
          variant="brick"
          onClick={updateTag}>
          Save
        </ButtonActivityIndicator>
      </div>
    </>
  );
};

const tagFormIsValid = (form: TagFormData) => {
  return form.title?.length > 3
    && form.title.length < 64
    && form.title.replace(/\s/g, '')?.length > 3;
};